import { Quat, Vec3 } from '../types'

/**
 * 向きブロック用の最小クォータニオン演算。Roblox の回転意味論に合わせる:
 * - 相対回転（CFrame *= CFrame.Angles(...)）は単軸の局所後掛け（currentFull * delta）。
 * - 絶対 Orientation(x,y,z)（度）は YXZ 順（Ry·Rx·Rz）で構築・抽出する。
 *
 * three を import せず純粋関数で完結させ、interpreter を軽量に保つ。
 */

const DEG = Math.PI / 180

/** 単軸（x/y/z）まわり rad のクォータニオン。 */
export const quatFromAxisAngle = (axis: 'x' | 'y' | 'z', rad: number): Quat => {
  const half = rad / 2
  const s = Math.sin(half)
  const c = Math.cos(half)
  if (axis === 'x') {
    return { x: s, y: 0, z: 0, w: c }
  }
  if (axis === 'y') {
    return { x: 0, y: s, z: 0, w: c }
  }
  return { x: 0, y: 0, z: s, w: c }
}

/** ハミルトン積 a⊗b（a を先に適用した姿勢に b を後掛け＝b は a 局所で回る）。 */
export const quatMultiply = (a: Quat, b: Quat): Quat => ({
  w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
  y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
  z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
})

/** Roblox Orientation(度, YXZ順 Ry·Rx·Rz) → クォータニオン。 */
export const quatFromRobloxOrientation = (
  xDeg: number,
  yDeg: number,
  zDeg: number,
): Quat =>
  quatMultiply(
    quatFromAxisAngle('y', yDeg * DEG),
    quatMultiply(
      quatFromAxisAngle('x', xDeg * DEG),
      quatFromAxisAngle('z', zDeg * DEG),
    ),
  )

/**
 * 回転した直方体（寸法 size）を包むワールド軸平行 AABB の half extents。
 * |R|·(size/2)（回転行列の成分絶対値 × 半寸法の積和）で求める。
 * 入力は単位想定だが、念のため正規化してから回転行列要素を作る。
 */
export const rotatedAabbHalfExtents = (q: Quat, size: Vec3): Vec3 => {
  const len = Math.hypot(q.x, q.y, q.z, q.w) || 1
  const x = q.x / len
  const y = q.y / len
  const z = q.z / len
  const w = q.w / len
  // 回転行列要素（row-major m_row_col）。
  const m11 = 1 - 2 * (y * y + z * z)
  const m12 = 2 * (x * y - w * z)
  const m13 = 2 * (x * z + w * y)
  const m21 = 2 * (x * y + w * z)
  const m22 = 1 - 2 * (x * x + z * z)
  const m23 = 2 * (y * z - w * x)
  const m31 = 2 * (x * z - w * y)
  const m32 = 2 * (y * z + w * x)
  const m33 = 1 - 2 * (x * x + y * y)
  const hx = size.x / 2
  const hy = size.y / 2
  const hz = size.z / 2
  return {
    x: Math.abs(m11) * hx + Math.abs(m12) * hy + Math.abs(m13) * hz,
    y: Math.abs(m21) * hx + Math.abs(m22) * hy + Math.abs(m23) * hz,
    z: Math.abs(m31) * hx + Math.abs(m32) * hy + Math.abs(m33) * hz,
  }
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))

/**
 * クォータニオン → Roblox Orientation(度)。YXZ 抽出（three.js Euler 'YXZ' と同式）。
 * 入力は単位想定だが、念のため正規化してから回転行列要素を作る。
 */
export const quatToRobloxOrientation = (
  q: Quat,
): { x: number; y: number; z: number } => {
  const len = Math.hypot(q.x, q.y, q.z, q.w) || 1
  const x = q.x / len
  const y = q.y / len
  const z = q.z / len
  const w = q.w / len
  // 回転行列要素（row-major m_row_col）。
  const m11 = 1 - 2 * (y * y + z * z)
  const m13 = 2 * (x * z + w * y)
  const m21 = 2 * (x * y + w * z)
  const m22 = 1 - 2 * (x * x + z * z)
  const m23 = 2 * (y * z - w * x)
  const m31 = 2 * (x * z - w * y)
  const m33 = 1 - 2 * (x * x + y * y)
  const rx = Math.asin(-clamp(m23, -1, 1))
  let ry: number
  let rz: number
  if (Math.abs(m23) < 0.9999999) {
    ry = Math.atan2(m13, m33)
    rz = Math.atan2(m21, m22)
  } else {
    ry = Math.atan2(-m31, m11)
    rz = 0
  }
  return { x: rx / DEG, y: ry / DEG, z: rz / DEG }
}
